import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

/**
 * Unified Login Page
 * - Single form for all users (no portal selection)
 * - Role-based automatic redirect
 * - 2FA support for admin/super_admin
 * - Generic error messages for security
 */
export const UnifiedLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loginData = {
        email: email.trim().toLowerCase(),
        password,
        remember_me: rememberMe,
        ...(showTwoFactor && { two_factor_code: twoFactorCode })
      };

      const user = await login(loginData.email, loginData.password, loginData.remember_me, loginData.two_factor_code);

      // Check if 2FA is required
      if (user.requires_2fa && !showTwoFactor) {
        setShowTwoFactor(true);
        setLoading(false);
        toast.info('Please enter your two-factor authentication code');
        return;
      }

      // Success message with optional 2FA setup warning
      if (user.requires_2fa_setup) {
        toast.warning(`Welcome back! Please set up two-factor authentication for your account security.`);
      } else {
        toast.success(`Welcome back, ${user.name || user.email}!`);
      }

      // Role-based automatic redirect (no client-side role checks)
      if (user.role === 'super_admin' || user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'agent') {
        navigate('/agent/dashboard');
      } else {
        navigate('/app/home');
      }
    } catch (error) {
      // Generic error messages - don't reveal account existence
      const errorMsg = error.response?.data?.detail || error.message;
      
      if (errorMsg.toLowerCase().includes('too many')) {
        // Show lockout message
        toast.error(errorMsg);
      } else if (errorMsg.toLowerCase().includes('two-factor') || errorMsg.toLowerCase().includes('2fa')) {
        // Invalid 2FA code
        toast.error('Invalid authentication code. Please try again.');
        setTwoFactorCode('');
      } else {
        // Generic error for everything else
        toast.error('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Brand */}
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1622737133809-d95047b9e673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjAzZCUyMHNoYXBlcyUyMGNsZWFyJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODMzNTQ5MzR8MA&ixlib=rb-4.1.0&q=85"
          alt="Abstract 3D"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/90 to-black/80 flex items-center justify-center p-12">
          <div className="max-w-lg">
            <h1 className="text-5xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Matrise
            </h1>
            <p className="text-xl text-zinc-300" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything you need, in one app. Pay, shop, get things done and connect — all with one Matrise account.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-6">
          <div>
            <h2 className="text-3xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="login-heading">
              Sign In
            </h2>
            <p className="mt-2 text-muted-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Enter your credentials to access your account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {!showTwoFactor ? (
              <>
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    data-testid="login-email-input"
                    className="h-11"
                    autoComplete="email"
                  />
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    data-testid="login-password-input"
                    className="h-11"
                    autoComplete="current-password"
                  />
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={setRememberMe}
                      data-testid="login-remember-checkbox"
                    />
                    <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                      Remember me
                    </Label>
                  </div>
                  <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              </>
            ) : (
              <>
                {/* 2FA Code Input */}
                <div className="space-y-2">
                  <Label htmlFor="twoFactorCode" className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Two-Factor Authentication Code
                  </Label>
                  <Input
                    id="twoFactorCode"
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
                    required
                    placeholder="000000"
                    data-testid="login-2fa-input"
                    className="h-11 text-center text-2xl tracking-widest"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the 6-digit code from your authenticator app, or use a backup code
                  </p>
                </div>

                {/* Back button */}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowTwoFactor(false);
                    setTwoFactorCode('');
                  }}
                  className="w-full"
                >
                  ← Back to login
                </Button>
              </>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-11"
              disabled={loading || (showTwoFactor && twoFactorCode.length < 6)}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : showTwoFactor ? 'Verify & Sign In' : 'Sign In'}
            </Button>
          </form>

          {/* Register Link */}
          {!showTwoFactor && (
            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="register-link">
                Sign up
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
